// The sponsor's pieces of interface (DESIGN §10). The words and the links are in sponsor.ts; this file only places them.

import { ExternalIcon } from './icons.js';
import { shareToOrganiser } from './share.js';
import { ABOUT, CARD, HOME_BYLINE, linkName, MENU_LINK, SONG_END, sponsorLink, type Placement } from './sponsor.js';

interface LinkProps {
  placement: Placement;
  /** What the link says, which is also the start of its accessible name. */
  text: string;
  class?: string;
  /** Draw the small "opens in a new tab" arrow after the words. */
  icon?: boolean;
}

/** A link to BailaBento: a new tab, no opener, and a name that says where it goes. */
export function SponsorLink({ placement, text, class: cls, icon }: LinkProps) {
  return (
    <a class={cls} href={sponsorLink(placement)} target="_blank" rel="noopener" aria-label={linkName(text)} data-placement={placement}>
      {text}
      {icon && <ExternalIcon />}
    </a>
  );
}

/** Home, under the title: "by BailaBento", beside the tagline. */
export function HomeByline() {
  return <SponsorLink placement="home_title" text={HOME_BYLINE} class="by" />;
}

/** Home: the permanent sponsor card between Your songs and the footer. */
export function SponsorCard() {
  return (
    <aside class="sponsor" aria-label="Sponsor" data-testid="sponsor-card">
      <p class="sponsor-head">
        <span class="sponsor-label">{CARD.label}</span>
        <span class="sponsor-name">{CARD.name}</span>
      </p>
      <p class="sponsor-line">{CARD.line}</p>
      <div class="sponsor-actions">
        <SponsorLink placement="home_card" text={CARD.see} class="btn sponsor-act" />
        <button type="button" class="btn sponsor-act" data-testid="sponsor-send" onClick={() => void shareToOrganiser()}>{CARD.send}</button>
      </div>
    </aside>
  );
}

/** The menu sheet's last line. */
export function MenuSponsor() {
  return (
    <li>
      <SponsorLink placement="menu" text={MENU_LINK} class="menu-item sponsor-menu" icon />
    </li>
  );
}

/** The stage's status line at the end of a song (the status line's own element; the caller puts it in place). */
export function SongEndLine() {
  return (
    <div class="status-line stage-extra" data-end="" data-testid="song-end">
      <span>{SONG_END.before}</span>
      <span class="nowrap"><SponsorLink placement="song_end" text={SONG_END.link} />{SONG_END.after}</span>
    </div>
  );
}

/** The About sheet: why salsacount is free, what BailaBento does, who it is priced for, and the link. */
export function AboutSponsor() {
  return (
    <section class="sponsor-about" aria-label="About BailaBento" data-testid="about-sponsor">
      {ABOUT.sentences.map((sentence) => <p key={sentence} class="sentence">{sentence}</p>)}
      <SponsorLink placement="about" text={ABOUT.link} class="btn about-link" icon />
    </section>
  );
}
